import React, { useState } from 'react';

import Icon, { LoadingOutlined } from '@ant-design/icons';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useStripe } from '@stripe/react-stripe-js';
import { StripeCardCvcElementChangeEvent, StripeCardExpiryElementChangeEvent, StripeCardNumberElement, StripeCardNumberElementChangeEvent } from '@stripe/stripe-js';
import { Button, Modal } from 'antd';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CrossSVG } from '../../../assets/sightx-icons/svg/icon-sx-cross.svg';
import { updatePaymentMethod } from '../../../slices/accounts/updatePaymentMethod/updatePaymentMethod.thunks';
import { selectors as updatePaymentSelectors } from '../../../slices/accounts/updatePaymentMethod/updatePaymentSelectors';
import { showErrorMessage } from '../../../slices/notifications';
import { colors } from '../../../styles/variables/index';

import styles from './index.module.scss';

const FIELD_OPTIONS = {
  style: {
    invalid: {
      color: colors.baseBlack,
      border: `1px solid ${colors.error500}`
    }
  }
};

interface UpdatePaymentModalProps {
  isOpen: boolean;
  onCancel: () => void;
}

type Error = {
  type: string;
  code?: string;
  message?: string;
}

const getErrorMessage = (error = ''): string => error.replace('Your card', 'Card');

export const UpdatePaymentModal: React.FunctionComponent<UpdatePaymentModalProps> = ({ isOpen, onCancel }) => {
  const [cardNumber, setCardNumber] = useState<StripeCardNumberElement | null>(null);
  const [errors, setErrors] = useState<Error[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isUpdatingPaymentMethod = useSelector(updatePaymentSelectors.selectUpdatingPaymentMethod);
  const stripe = useStripe();
  const dispatch = useDispatch();

  const handleReady = (element: StripeCardNumberElement) => setCardNumber(element);

  const handleChange = (event: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent) => {
    if (event.elementType === 'cardExpiry' && !event.error) {
      setErrors(errors.filter(error => !error?.code?.includes('expiry')));
    }
    if (event.elementType === 'cardNumber' && !event.error) {
      setErrors(errors.filter(error => !error?.code?.includes('number')));
    }
    if (event.elementType === 'cardCvc' && !event.error) {
      setErrors(errors.filter(error => !error?.code?.includes('cvc')));
    }
    if (event.error) {
      setErrors([...errors, event.error]);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors([]);
    setIsLoading(true);
    if(!stripe || !cardNumber) {
      return;
    }
    const result = await stripe
      .createPaymentMethod({
        type: 'card',
        card: cardNumber
      });
    if (result.error) {
      setErrors([result.error]);
      dispatch(showErrorMessage(result.error.message ?? 'Unexpected error'));
      setIsLoading(false);
      return;
    }
    dispatch(updatePaymentMethod({ paymentMethodId: result.paymentMethod.id }));
    setIsLoading(false);
  };

  if (!stripe) {
    return <LoadingOutlined />;
  }

  const numberError = errors.find(error => error?.code?.includes('number'));
  const expiryError = errors.find(error => error?.code?.includes('expiry'));
  const cvcError = errors.find(error => error?.code?.includes('cvc'));

  const isUpdatingPayment = isUpdatingPaymentMethod || isLoading;
  return <Modal
    title="Update Credit Card"
    centered
    visible={isOpen}
    onCancel={onCancel}
    className={styles.modal}
    closeIcon={<Icon component={CrossSVG} className={styles.closeIcon}/>}
    width={450}
    footer={null}
    maskClosable={!isUpdatingPayment}
    closable={!isUpdatingPayment}
  >
    <form onSubmit={handleSubmit}>
      <div className={styles.field}>
        <label htmlFor="cardNumber">Card number</label>
        <CardNumberElement
          onReady={handleReady}
          onChange={handleChange}
          className={cx(styles.fieldInput, {
            [styles.error]: numberError
          })}
          options={{
            showIcon: true,
            disabled: isUpdatingPayment,
            ...FIELD_OPTIONS
          }}
        />
        {numberError && <div className={styles.errorMessage}>{getErrorMessage(numberError.message)}</div>}
      </div>
      <div className={styles.extras}>
        <div className={styles.field}>
          <label htmlFor="expiry">Expiration</label>
          <CardExpiryElement
            onChange={handleChange}
            className={cx(styles.fieldInput, {
              [styles.error]: expiryError
            })}
            options={{
              disabled: isUpdatingPayment,
              ...FIELD_OPTIONS
            }}
          />
          {expiryError && <div className={styles.errorMessage}>{getErrorMessage(expiryError.message)}</div>}
        </div>
        <div className={styles.field}>
          <label htmlFor="cvc">CVC</label>
          <CardCvcElement
            onChange={handleChange}
            className={cx(styles.fieldInput, {
              [styles.error]: cvcError
            })}
            options={{
              disabled: isUpdatingPayment,
              ...FIELD_OPTIONS
            }}
          />
          {cvcError && <div className={styles.errorMessage}>{getErrorMessage(cvcError.message)}</div>}
        </div>
      </div>
      <div className={styles.actions}>
        <Button onClick={onCancel} disabled={isUpdatingPayment}>Cancel</Button>
        <Button
          type='primary'
          htmlType='submit'
          disabled={!stripe || !cardNumber || isUpdatingPayment}
        >
          Save
        </Button>
      </div>
    </form>
  </Modal>;
};